<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    :return-value.sync="date"
    transition="scale-transition"
    offset-y
    min-width="290px"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        :value="dateRangeText"
        :hide-details="true"
        prepend-icon="icon-calendar"
        readonly
        v-on="on"
        class="date-input"
      ></v-text-field>
    </template>
    <v-date-picker
      v-model="date"
      first-day-of-week="1"
      :locale="selectedLocale"
      no-title
      scrollable
      range
      color="primary"
      class="pa-2"
    >
      <div class="full-width">
        <div class="dates-select">
          <v-text-field
            v-model="dateFrom"
            @keyup.enter="handleEditDatesBtn"
            :label="$t('message.common.dateFrom')"
            :disabled="!editDates"
          ></v-text-field
          ><v-text-field
            v-model="dateTo"
            @keyup.enter="handleEditDatesBtn"
            :label="$t('message.common.dateTo')"
            :disabled="!editDates"
          ></v-text-field>
          <v-btn icon color="primary" @click="handleEditDatesBtn">
            <v-icon>
              {{ editDates ? "icon-check" : "icon-edit" }}
            </v-icon>
          </v-btn>
        </div>
        <div class="compare-select">
          <span class="mr-2 text-no-wrap mb-3">{{
            $t("message.common.compareWith")
          }}</span>
          <v-select
            :items="comparePeriodItems"
            v-model="tmpPeriodValue"
            dense
          ></v-select>
        </div>
        <div class="d-flex justify-space-between">
          <ui-button outlined color="primary" @click="menu = false">
            {{ $t("message.common.cancel") }}
          </ui-button>
          <v-spacer />
          <ui-button color="primary" @click="handleDatePickerOk">
            {{ $t("message.common.ok") }}
          </ui-button>
        </div>
      </div>
    </v-date-picker>
  </v-menu>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import { PERIOD } from "@/enums/period.enum";
import { COMPARE_PERIODS_ENUM } from "@/enums/comparePeriods.enum";
import { FORMAT } from "@/config/date-format";
import { sortDateRangeAsc } from "@/helpers/helpers";
import { gtmEvent__filterDatePeriod } from "@/helpers/gtm-events";

export default {
  name: "ReviewsAnalystFilters",
  data() {
    return {
      dateFrom: moment()
        .subtract(1, PERIOD.MONTH)
        .add(1, PERIOD.DAY)
        .format(FORMAT.DATE),
      dateTo: moment().format(FORMAT.DATE),
      editDates: false,
      dateInputType: null,
      secondDate: null,
      menu: false,
      comparePeriodItems: [
        {
          text: this.$t("message.analytics.businessAddresses.noCompare"),
          value: COMPARE_PERIODS_ENUM.NO_COMPARE
        },
        {
          text: this.$t(
            "message.analytics.businessAddresses.compareWithPreviousPeriod"
          ),
          value: COMPARE_PERIODS_ENUM.COMPARE_WITH_PREVIOUS_PERIOD
        },
        {
          text: this.$t(
            "message.analytics.businessAddresses.compareWithPreviousYear"
          ),
          value: COMPARE_PERIODS_ENUM.COMPARE_WITH_PREVIOUS_YEAR
        }
      ],
      comparePeriodValue: COMPARE_PERIODS_ENUM.NO_COMPARE,
      tmpPeriodValue: COMPARE_PERIODS_ENUM.NO_COMPARE
    };
  },
  props: {
    firstDate: {
      type: Array,
      default: () => [
        moment()
          .subtract(1, PERIOD.MONTH)
          .add(1, PERIOD.DAY)
          .format(FORMAT.DATE),
        moment().format(FORMAT.DATE)
      ]
    }
  },
  computed: {
    dateRangeText() {
      if (this.date.length > 1) {
        return this.date.join(" ~ ");
      }
      return this.date[0];
    },
    dateRange() {
      return this.date.length === 1 ? [this.date[0], this.date[0]] : this.date;
    },
    date: {
      get() {
        return this.firstDate;
      },
      set(value) {
        this.$emit("update:firstDate", sortDateRangeAsc(value));
      }
    },
    ...mapState({
      selectedLocale: state => state.settings.selectedLocale
    })
  },
  watch: {
    date(newDate) {
      if (newDate[0] && this.dateFrom !== newDate[0]) {
        this.dateFrom = newDate[0];
      }
      if (newDate[1] && this.dateTo !== newDate[1]) {
        this.dateTo = newDate[1];
      }

      this.dateInputType = "calendar";
    },
    menu(value) {
      if (!value) {
        this.tmpPeriodValue = this.comparePeriodValue;
      }
    }
  },
  methods: {
    async handleDatePickerOk() {
      if (this.date.length === 1) {
        this.$emit("update:firstDate", [this.date[0], this.date[0]]);
      }

      await this.$nextTick(() => {
        this.$refs.menu.save(this.date);
        this.setSecondPeriod();
        this.onChange();
      });

      gtmEvent__filterDatePeriod({
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        dateInputType: this.dateInputType,
        compareWith: Object.keys(COMPARE_PERIODS_ENUM)
          .find(key => COMPARE_PERIODS_ENUM[key] === this.comparePeriodValue)
          .toLowerCase()
      });
    },
    setSecondPeriod() {
      this.comparePeriodValue = this.tmpPeriodValue;

      if (this.comparePeriodValue === COMPARE_PERIODS_ENUM.NO_COMPARE) {
        this.secondDate = null;
      }

      if (
        this.comparePeriodValue ===
        COMPARE_PERIODS_ENUM.COMPARE_WITH_PREVIOUS_YEAR
      ) {
        let [from, to] = this.date;
        to = to || from;
        this.secondDate = [
          moment(from)
            .subtract(1, PERIOD.YEAR)
            .format(FORMAT.DATE),
          moment(to)
            .subtract(1, PERIOD.YEAR)
            .format(FORMAT.DATE)
        ];
      }

      if (
        this.comparePeriodValue ===
        COMPARE_PERIODS_ENUM.COMPARE_WITH_PREVIOUS_PERIOD
      ) {
        const [from, to] = this.date;
        const diff = to ? moment(to).diff(moment(from), PERIOD.DAYS) : 0;
        this.secondDate = [
          moment(from)
            .subtract(1 + diff, PERIOD.DAY)
            .format(FORMAT.DATE),
          moment(from)
            .subtract(1, PERIOD.DAY)
            .format(FORMAT.DATE)
        ];
      }
    },

    handleEditDatesBtn() {
      if (this.editDates) {
        this.setDates();
      }
      this.editDates = !this.editDates;
    },

    async setDates() {
      const oldDate = this.date;

      if (!moment(this.dateFrom).isValid()) {
        this.dateFrom = oldDate[0];
      }
      if (!moment(this.dateTo).isValid()) {
        this.dateTo = oldDate[1];
      }

      this.$emit("update:firstDate", [this.dateFrom, this.dateTo]);

      await this.$nextTick(() => {
        this.dateInputType = "manual";
      });
    },

    onChange() {
      const periods = {
        first: this.dateRange,
        second: this.secondDate
      };

      this.$emit("change", periods);
    }
  }
};
</script>

<style lang="scss" scoped>
.dates-select {
  display: flex;
  align-items: center;
  padding: $spacing 0;
  border-top: 1px solid $light-3;
  margin-bottom: $spacing;

  .v-text-field {
    width: 100px;
    margin-bottom: -$spacing * 2;
  }

  & > :not(:last-child) {
    margin-right: $spacing;
  }
}

.compare-select {
  display: flex;
  align-items: center;
  max-width: 275px;
}

.filters {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.date-input {
  min-width: 230px;
  padding-top: 0;
}
</style>
